import { createContext, useContext, useState, useCallback, useEffect, useRef } from 'react';
import axios from 'axios';

const API = process.env.REACT_APP_BACKEND_URL ? `${process.env.REACT_APP_BACKEND_URL}/api` : '/api';
const AppContext = createContext(null);
const PERSONA_KEY = 'sankhya_active_persona_id';
const LAST_SEARCH_KEY = 'sankhya_last_search_id';

// Set default API key header for all axios requests (if configured)
if (process.env.REACT_APP_API_KEY) {
  axios.defaults.headers.common['X-API-Key'] = process.env.REACT_APP_API_KEY;
}

export function AppProvider({ children }) {
  const [profile, setProfile] = useState(null);
  const [jobs, setJobs] = useState([]);
  const [totalJobsCount, setTotalJobsCount] = useState(0);
  const [currentPage, setCurrentPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [pageSize] = useState(50);
  const [selectedJob, setSelectedJob] = useState(null);
  const [stats, setStats] = useState(null);
  const [searchHistory, setSearchHistory] = useState([]);
  const [documents, setDocuments] = useState([]);
  const [loading, setLoading] = useState({});
  const [lastSearchId, setLastSearchId] = useState(() => localStorage.getItem(LAST_SEARCH_KEY) || null);
  const [lastSearchResult, setLastSearchResult] = useState(() => {
    try { return JSON.parse(localStorage.getItem('sankhya_last_search_result') || 'null'); } catch { return null; }
  });
  const [unreadAlerts, setUnreadAlerts] = useState(0);
  const [error, setError] = useState(null);
  const [scoringProgress, setScoringProgress] = useState(null);
  const scoringPollRef = useRef(null);

  // Persona state
  const [personas, setPersonas] = useState([]);
  const [activePersonaId, setActivePersonaIdState] = useState(
    () => localStorage.getItem(PERSONA_KEY) || null
  );
  const [showPersonaSelector, setShowPersonaSelector] = useState(false);
  const [personasReady, setPersonasReady] = useState(false);

  const activePersona = personas.find(p => p.id === activePersonaId) || null;

  const setLoadingKey = (key, val) => setLoading(l => ({ ...l, [key]: val }));

  const setActivePersonaId = useCallback((id) => {
    setActivePersonaIdState(id);
    if (id) localStorage.setItem(PERSONA_KEY, id);
    else localStorage.removeItem(PERSONA_KEY);
  }, []);

  // Auth State
  const [token, setToken] = useState(() => localStorage.getItem('sankhya_token'));
  const [user, setUser] = useState(() => JSON.parse(localStorage.getItem('sankhya_user')) || null);

  const isAdmin = user?.role === 'admin';

  useEffect(() => {
    if (token) {
      axios.defaults.headers.common['Authorization'] = `Bearer ${token}`;
      localStorage.setItem('sankhya_token', token);
    } else {
      delete axios.defaults.headers.common['Authorization'];
      localStorage.removeItem('sankhya_token');
    }
  }, [token]);

  useEffect(() => {
    if (user) {
      localStorage.setItem('sankhya_user', JSON.stringify(user));
    } else {
      localStorage.removeItem('sankhya_user');
    }
  }, [user]);

  const login = useCallback(async (email, password) => {
    setLoadingKey('auth', true);
    try {
      const res = await axios.post(`${API}/auth/login`, { email, password });
      setToken(res.data.access_token);
      setUser(res.data.user);
      return res.data;
    } finally {
      setLoadingKey('auth', false);
    }
  }, []);

  const register = useCallback(async (email, password, inviteToken, fullName = '') => {
    setLoadingKey('auth', true);
    try {
      const res = await axios.post(`${API}/auth/register`, {
        email, password, invite_token: inviteToken, full_name: fullName,
      });
      setToken(res.data.access_token);
      setUser(res.data.user);
      return res.data;
    } finally {
      setLoadingKey('auth', false);
    }
  }, []);

  const logout = useCallback(async () => {
    try { await axios.post(`${API}/auth/logout`); } catch { /* no-op — JWT is stateless */ }
    setToken(null);
    setUser(null);
    setActivePersonaId(null);
    setPersonas([]);
  }, [setActivePersonaId]);

  // Persona API
  const fetchPersonas = useCallback(async () => {
    try {
      const res = await axios.get(`${API}/personas`);
      const list = res.data.personas || [];
      setPersonas(list);
      return list;
    } catch (e) { return []; }
  }, []);

  const createPersona = useCallback(async (data) => {
    setLoadingKey('createPersona', true);
    try {
      const res = await axios.post(`${API}/personas`, data);
      if (!res.data.error) {
        setPersonas(prev => [...prev, res.data]);
      }
      return res.data;
    } finally { setLoadingKey('createPersona', false); }
  }, []);

  const updatePersona = useCallback(async (id, data) => {
    // v7.7.0 P2-3 — `data` may include `min_score_ignore` / `min_score_save`
    // (0-100). Server COALESCEs nulls so omitted fields preserve existing
    // values; old clients calling without the new fields stay backward compatible.
    const res = await axios.put(`${API}/personas/${id}`, data);
    return res.data;
  }, []);

  const deletePersona = useCallback(async (id) => {
    try {
      await axios.delete(`${API}/personas/${id}`);
      setPersonas(prev => prev.filter(p => p.id !== id));
      if (activePersonaId === id) {
        setActivePersonaId(null);
      }
    } catch (e) {}
  }, [activePersonaId, setActivePersonaId]);

  const uploadPersonaResume = useCallback(async (personaId, file) => {
    const formData = new FormData();
    formData.append('file', file);
    setLoadingKey('resume', true);
    try {
      const res = await axios.post(`${API}/personas/${personaId}/resume`, formData);
      setPersonas(prev => prev.map(p => p.id === personaId
        ? { ...p, has_resume: true, profile: res.data.profile }
        : p
      ));
      setProfile(res.data.profile);
      return res.data;
    } finally { setLoadingKey('resume', false); }
  }, []);

  // Resume (persona-aware)
  const fetchProfile = useCallback(async () => {
    setLoadingKey('profile', true);
    try {
      if (activePersonaId) {
        try {
          const res = await axios.get(`${API}/personas/${activePersonaId}/profile`);
          if (res.data != null && typeof res.data === 'object' && Object.keys(res.data).length > 0 && !res.data.error) {
            setProfile(res.data);
            return;
          }
        } catch (e) { /* persona fetch failed — fall through to user-level */ }
      }
      const res = await axios.get(`${API}/resume/profile`);
      if (res.data && typeof res.data === 'object' && Object.keys(res.data).length > 0) {
        setProfile(res.data);
      }
    } catch (e) { /* user profile fetch also failed */ }
    finally { setLoadingKey('profile', false); }
  }, [activePersonaId]);

  const uploadResume = useCallback(async (file) => {
    if (activePersonaId) {
      return uploadPersonaResume(activePersonaId, file);
    }
    const formData = new FormData();
    formData.append('file', file);
    setLoadingKey('resume', true);
    try {
      const res = await axios.post(`${API}/resume/upload`, formData);
      setProfile(res.data);
      return res.data;
    } finally { setLoadingKey('resume', false); }
  }, [activePersonaId, uploadPersonaResume]);

  // Job Search (persona-aware) — now async with polling
  const searchJobs = useCallback(async (params) => {
    setLoadingKey('search', true);
    try {
      const payload = activePersonaId ? { ...params, persona_id: activePersonaId } : params;
      const res = await axios.post(`${API}/jobs/search`, payload);
      const searchId = res.data.search_id;
      setLastSearchId(searchId);
      localStorage.setItem(LAST_SEARCH_KEY, searchId);

      // Poll for completion. Worker subprocess timeout = 300s; allow a buffer
      // for status writes + post-processing. 110 * 3s = 5.5 minutes.
      let attempts = 0;
      const maxAttempts = 110;
      while (attempts < maxAttempts) {
        await new Promise(r => setTimeout(r, 3000));
        attempts++;
        try {
          const statusRes = await axios.get(`${API}/jobs/search/${searchId}/status`);
          const st = statusRes.data;
          if (st.status === "complete" || st.status === "error") {
            // Fetch the results
            const jobsRes = await axios.get(`${API}/jobs?search_id=${searchId}&page=1&page_size=50`);
            const data = jobsRes.data;
            setJobs(data.jobs || []);
            setTotalJobsCount(data.total || 0);
            setCurrentPage(1);
            setTotalPages(data.total_pages || 1);
            const result = {
              search_id: searchId,
              search_terms: st.search_terms || res.data.search_terms,
              total: st.jobs_found || data.total || 0,
              jobs: data.jobs || [],
              auto_scoring: st.auto_scoring || false,
              source_breakdown: st.source_breakdown || {},
              status: st.status,
              error: st.error || "",
            };
            setLastSearchResult(result);
            try { localStorage.setItem('sankhya_last_search_result', JSON.stringify(result)); } catch {}
            return result;
          }
        } catch (pollErr) {
          // Continue polling on transient errors
        }
      }
      return { ...res.data, status: "timeout" };
    } finally { setLoadingKey('search', false); }
  }, [activePersonaId]);

  // Paginated fetch
  const fetchJobs = useCallback(async (query = {}, page = 1) => {
    setLoadingKey('fetchJobs', true);
    try {
      const params = new URLSearchParams();
      Object.entries(query).forEach(([k, v]) => { if (v) params.set(k, v); });
      params.set('page', page);
      params.set('page_size', '50');
      const res = await axios.get(`${API}/jobs?${params}`);
      const data = res.data;
      setJobs(data.jobs || []);
      setTotalJobsCount(data.total || 0);
      setCurrentPage(data.page || 1);
      setTotalPages(data.total_pages || 1);
      return data;
    } catch (e) {
      return { jobs: [], total: 0, page: 1, total_pages: 1 };
    } finally { setLoadingKey('fetchJobs', false); }
  }, []);

  const updateJobStatus = useCallback(async (jobId, status) => {
    try {
      const res = await axios.put(`${API}/jobs/${jobId}/status`, { status });
      setJobs(prev => prev.map(j => j.id === jobId ? { ...j, ...res.data } : j));
      if (selectedJob?.id === jobId) setSelectedJob(prev => ({ ...prev, ...res.data }));
      return res.data;
    } catch (e) {}
  }, [selectedJob]);

  const scoreJob = useCallback(async (jobId) => {
    setLoadingKey(`score-${jobId}`, true);
    try {
      const qp = activePersonaId ? `?persona_id=${activePersonaId}` : '';
      const res = await axios.post(`${API}/jobs/${jobId}/score${qp}`);
      setJobs(prev => prev.map(j => j.id === jobId ? { ...j, ...res.data } : j));
      if (selectedJob?.id === jobId) setSelectedJob(prev => ({ ...prev, ...res.data }));
      return res.data;
    } finally { setLoadingKey(`score-${jobId}`, false); }
  }, [selectedJob, activePersonaId]);

  const scoreBatch = useCallback(async (searchId) => {
    setLoadingKey('scoreBatch', true);
    try {
      const qp = activePersonaId ? `&persona_id=${activePersonaId}` : '';
      const res = await axios.post(`${API}/jobs/score-batch?search_id=${searchId}${qp}`);
      if (searchId) {
        const jobsRes = await axios.get(`${API}/jobs?search_id=${searchId}&page=1&page_size=50`);
        const data = jobsRes.data;
        setJobs(data.jobs || []);
        setTotalJobsCount(data.total || 0);
        setCurrentPage(1);
        setTotalPages(data.total_pages || 1);
      }
      return res.data;
    } finally { setLoadingKey('scoreBatch', false); }
  }, [activePersonaId]);

  // Score ALL unscored jobs
  const scoreAllUnscored = useCallback(async () => {
    setLoadingKey('scoreAll', true);
    try {
      const res = await axios.post(`${API}/jobs/score-all`);
      if (res.data.unscored > 0) {
        startScoringPoll();
      }
      return res.data;
    } finally { setLoadingKey('scoreAll', false); }
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  // Scoring status polling
  const fetchScoringStatus = useCallback(async () => {
    try {
      const res = await axios.get(`${API}/jobs/scoring-status`);
      setScoringProgress(res.data);
      return res.data;
    } catch (e) { return null; }
  }, []);

  const startScoringPoll = useCallback(() => {
    if (scoringPollRef.current) clearInterval(scoringPollRef.current);
    scoringPollRef.current = setInterval(async () => {
      const status = await fetchScoringStatus();
      if (status && status.unscored === 0) {
        clearInterval(scoringPollRef.current);
        scoringPollRef.current = null;
        setScoringProgress(null);
      }
    }, 5000);
  }, [fetchScoringStatus]);

  const stopScoringPoll = useCallback(() => {
    if (scoringPollRef.current) {
      clearInterval(scoringPollRef.current);
      scoringPollRef.current = null;
    }
  }, []);

  const generateContent = useCallback(async (jobId, type) => {
    const endpoints = { cover_letter: 'cover-letter', cold_email: 'cold-email', linkedin_message: 'linkedin-message' };
    setLoadingKey(`gen-${type}`, true);
    try {
      const res = await axios.post(`${API}/generate/${endpoints[type]}`, { job_id: jobId });
      return res.data;
    } finally { setLoadingKey(`gen-${type}`, false); }
  }, []);

  const fetchStats = useCallback(async () => {
    try {
      const res = await axios.get(`${API}/stats`);
      setStats(res.data);
    } catch (e) {}
  }, []);

  const fetchScoreDistribution = useCallback(async () => {
    try {
      const res = await axios.get(`${API}/stats/score-distribution`);
      return res.data?.distribution || [];
    } catch (e) { return []; }
  }, []);

  const fetchSearchHistory = useCallback(async () => {
    try {
      const res = await axios.get(`${API}/search-history`);
      setSearchHistory(res.data || []);
    } catch (e) {}
  }, []);

  const fetchDocuments = useCallback(async (jobId = null) => {
    try {
      const url = jobId ? `${API}/documents?job_id=${jobId}` : `${API}/documents`;
      const res = await axios.get(url);
      setDocuments(res.data || []);
    } catch (e) {}
  }, []);

  const deleteJob = useCallback(async (jobId) => {
    try {
      await axios.delete(`${API}/jobs/${jobId}`);
      setJobs(prev => prev.filter(j => j.id !== jobId));
      setTotalJobsCount(prev => Math.max(0, prev - 1));
      if (selectedJob?.id === jobId) setSelectedJob(null);
    } catch (e) {}
  }, [selectedJob]);

  const researchCompany = useCallback(async (companyName) => {
    setLoadingKey('research', true);
    try {
      const res = await axios.post(`${API}/research/company`, { company_name: companyName });
      return res.data;
    } finally { setLoadingKey('research', false); }
  }, []);

  const findHiringManagers = useCallback(async (companyName, roleTitle = '') => {
    setLoadingKey('contacts', true);
    try {
      const res = await axios.post(`${API}/research/hiring-manager`, { company_name: companyName, role_title: roleTitle });
      return res.data;
    } finally { setLoadingKey('contacts', false); }
  }, []);

  const scrapeCareerPage = useCallback(async (url) => {
    setLoadingKey('scrape', true);
    try {
      const res = await axios.post(`${API}/scrape/career-page`, { url });
      return res.data;
    } finally { setLoadingKey('scrape', false); }
  }, []);

  const deepScrape = useCallback(async (url) => {
    setLoadingKey('deepScrape', true);
    try {
      const res = await axios.post(`${API}/scrape/deep`, { url });
      return res.data;
    } finally { setLoadingKey('deepScrape', false); }
  }, []);

  const fetchSystemStatus = useCallback(async () => {
    try {
      const res = await axios.get(`${API}/system/status`);
      return res.data;
    } catch (e) { return null; }
  }, []);

  const fetchAlertCount = useCallback(async () => {
    try {
      const res = await axios.get(`${API}/alerts/count`);
      setUnreadAlerts(res.data.unread_count || 0);
    } catch (e) {}
  }, []);

  const fetchAlerts = useCallback(async (unreadOnly = false) => {
    try {
      const res = await axios.get(`${API}/alerts?unread_only=${unreadOnly}`);
      return res.data;
    } catch (e) { return { alerts: [], unread_count: 0 }; }
  }, []);

  const markAlertsRead = useCallback(async (alertIds = []) => {
    try {
      const res = await axios.post(`${API}/alerts/mark-read`, { alert_ids: alertIds });
      setUnreadAlerts(res.data?.unread_count || 0);
      return res.data;
    } catch (e) { return null; }
  }, []);

  const deleteAlert = useCallback(async (alertId) => {
    try { await axios.delete(`${API}/alerts/${alertId}`); } catch (e) {}
  }, []);

  // Watchlist API
  const fetchWatchlist = useCallback(async (personaId = null) => {
    try {
      const qp = personaId ? `?persona_id=${personaId}` : '';
      const res = await axios.get(`${API}/watchlist${qp}`);
      return res.data;
    } catch (e) { return { companies: [], count: 0 }; }
  }, []);

  const addWatchlistCompany = useCallback(async (data) => {
    setLoadingKey('watchlist', true);
    try {
      const res = await axios.post(`${API}/watchlist`, data);
      return res.data;
    } finally { setLoadingKey('watchlist', false); }
  }, []);

  const removeWatchlistCompany = useCallback(async (companyId) => {
    try {
      await axios.delete(`${API}/watchlist/${companyId}`);
    } catch (e) {}
  }, []);

  const toggleWatchlistPause = useCallback(async (companyId) => {
    try {
      const res = await axios.put(`${API}/watchlist/${companyId}/pause`);
      return res.data;
    } catch (e) { return null; }
  }, []);

  const scanWatchlistCompany = useCallback(async (companyId) => {
    setLoadingKey(`scan-${companyId}`, true);
    try {
      const res = await axios.post(`${API}/watchlist/${companyId}/scan`);
      return res.data;
    } finally { setLoadingKey(`scan-${companyId}`, false); }
  }, []);

  const fetchWatchlistJobs = useCallback(async (companyId) => {
    try {
      const res = await axios.get(`${API}/watchlist/jobs/${companyId}`);
      return res.data;
    } catch (e) { return { jobs: [], count: 0 }; }
  }, []);

  // Settings API
  const fetchSettings = useCallback(async () => {
    try {
      const res = await axios.get(`${API}/settings`);
      return res.data;
    } catch (e) { return null; }
  }, []);

  const saveSettings = useCallback(async (settings) => {
    setLoadingKey('settings', true);
    try {
      const res = await axios.post(`${API}/settings`, settings);
      return res.data;
    } finally { setLoadingKey('settings', false); }
  }, []);

  // Auto-Apply API
  const fetchAutoApplyCandidates = useCallback(async () => {
    try {
      const res = await axios.get(`${API}/auto-apply/candidates`);
      return res.data;
    } catch (e) { return { candidates: [], count: 0, min_score: 85 }; }
  }, []);

  const scanAllWatchlist = useCallback(async () => {
    setLoadingKey('scanAll', true);
    try {
      const res = await axios.post(`${API}/watchlist/scan-all`);
      return res.data;
    } finally { setLoadingKey('scanAll', false); }
  }, []);

  const fetchAutomationStatus = useCallback(async () => {
    try {
      const res = await axios.get(`${API}/automation/status`);
      return res.data;
    } catch (e) { return null; }
  }, []);

  const fetchActivityLog = useCallback(async (limit = 50, action = null, status = null) => {
    try {
      const params = new URLSearchParams({ limit });
      if (action) params.set('action', action);
      if (status) params.set('status', status);
      const res = await axios.get(`${API}/activity-log?${params}`);
      return res.data;
    } catch (e) { return { logs: [], count: 0 }; }
  }, []);

  const getRadarConfig = useCallback(async () => {
    try {
      const res = await axios.get(`${API}/radar/config`);
      return res.data;
    } catch (e) { return null; }
  }, []);

  const saveRadarConfig = useCallback(async (config) => {
    try {
      const res = await axios.post(`${API}/radar/config`, config);
      return res.data;
    } catch (e) { return null; }
  }, []);

  const runRadarNow = useCallback(async () => {
    setLoadingKey('radar', true);
    try {
      const res = await axios.post(`${API}/radar/run-now`);
      await fetchAlertCount();
      return res.data;
    } finally { setLoadingKey('radar', false); }
  }, [fetchAlertCount]);

  // Admin APIs
  const inviteUser = useCallback(async (email, note = '') => {
    setLoadingKey('invite', true);
    try {
      const res = await axios.post(`${API}/admin/invite`, { email, note });
      return res.data;
    } finally { setLoadingKey('invite', false); }
  }, []);

  const listUsers = useCallback(async () => {
    try {
      const res = await axios.get(`${API}/admin/users`);
      return res.data;
    } catch (e) { return { users: [] }; }
  }, []);

  const getAdminUser = useCallback(async (userId) => {
    const res = await axios.get(`${API}/admin/users/${userId}`);
    return res.data;
  }, []);

  const disableUser = useCallback(async (userId) => {
    const res = await axios.post(`${API}/admin/users/${userId}/disable`);
    return res.data;
  }, []);

  const enableUser = useCallback(async (userId) => {
    const res = await axios.post(`${API}/admin/users/${userId}/enable`);
    return res.data;
  }, []);

  const deleteAdminUser = useCallback(async (userId, confirmEmail) => {
    const res = await axios.delete(`${API}/admin/users/${userId}`, {
      data: { confirm_email: confirmEmail },
    });
    return res.data;
  }, []);

  const adminGetResume = useCallback(async (userId, personaId) => {
    const res = await axios.get(`${API}/admin/users/${userId}/resume/${personaId}`);
    return res.data;
  }, []);

  const fetchAdminTelemetry = useCallback(async (type = 'health') => {
    const endpoints = {
      health: 'system-health',
      usage: 'llm-usage?days=30',
      queries: 'slow-queries?limit=20'
    };
    try {
      const res = await axios.get(`${API}/admin/${endpoints[type]}`);
      return res.data;
    } catch (e) { return null; }
  }, []);

  const fetchCeleryHealth = useCallback(async () => {
    try {
      const res = await axios.get(`${API}/admin/celery-health`);
      return res.data;
    } catch { return null; }
  }, []);

  const fetchSkillGraphStats = useCallback(async () => {
    try {
      const res = await axios.get(`${API}/admin/skill-graph-stats`);
      return res.data;
    } catch { return null; }
  }, []);

  const rebuildSkillGraph = useCallback(async (personaId) => {
    try {
      const res = await axios.post(`${API}/admin/rebuild-skill-graph/${personaId}`);
      return res.data;
    } catch { return null; }
  }, []);

  const fetchResendUsage = useCallback(async () => {
    try {
      const res = await axios.get(`${API}/admin/resend-usage`);
      return res.data;
    } catch { return null; }
  }, []);

  const fetchLLMConfig = useCallback(async () => {
    try {
      const res = await axios.get(`${API}/admin/llm-config`);
      return res.data?.configs ?? res.data ?? [];
    } catch { return null; }
  }, []);

  const updateLLMConfig = useCallback(async (provider, updates) => {
    try {
      const res = await axios.put(`${API}/admin/llm-config/${provider}`, updates);
      return res.data ? true : false;
    } catch { return false; }
  }, []);

  const overrideCircuitBreaker = useCallback(async (provider, action) => {
    const res = await axios.post(`${API}/admin/circuit-breaker/${provider}/override`, { action });
    return res.data;
  }, []);

  // PR5: Admin cron / radar management
  const fetchCronJobs = useCallback(async () => {
    try {
      const res = await axios.get(`${API}/admin/cron-jobs`);
      return res.data;
    } catch { return null; }
  }, []);

  const adminRunRadarNow = useCallback(async () => {
    const res = await axios.post(`${API}/admin/cron/radar/run-now`);
    return res.data;
  }, []);

  const adminRunWatchlistNow = useCallback(async () => {
    const res = await axios.post(`${API}/admin/cron/watchlist/run-now`);
    return res.data;
  }, []);

  const adminSaveRadarConfig = useCallback(async (updates) => {
    const res = await axios.post(`${API}/admin/cron/radar/config`, updates);
    return res.data;
  }, []);

  const adminSaveWatchlistConfig = useCallback(async (updates) => {
    const res = await axios.post(`${API}/admin/cron/watchlist/config`, updates);
    return res.data;
  }, []);

  const adminSaveAutoApplyConfig = useCallback(async (updates) => {
    const res = await axios.post(`${API}/admin/cron/auto-apply/config`, updates);
    return res.data;
  }, []);

  const fetchLLMEngineConfig = useCallback(async () => {
    try {
      const res = await axios.get(`${API}/admin/llm-engine-config`);
      return res.data;
    } catch { return null; }
  }, []);

  const saveLLMEngineConfig = useCallback(async (config) => {
    const res = await axios.put(`${API}/admin/llm-engine-config`, config);
    return res.data;
  }, []);

  const fetchAlertStats = useCallback(async () => {
    try {
      const res = await axios.get(`${API}/admin/cron/alert-stats`);
      return res.data;
    } catch { return null; }
  }, []);

  const fetchApiUsage = useCallback(async () => {
    try {
      const res = await axios.get(`${API}/admin/api-usage`);
      return res.data;
    } catch { return null; }
  }, []);

  const fetchScraperToggles = useCallback(async () => {
    try {
      const res = await axios.get(`${API}/admin/scraper-toggles`);
      return res.data;
    } catch { return null; }
  }, []);

  const updateScraperToggles = useCallback(async (toggles) => {
    const res = await axios.put(`${API}/admin/scraper-toggles`, toggles);
    return res.data;
  }, []);

  const invalidateSearchCache = useCallback(async (pattern = "search:*") => {
    const res = await axios.post(`${API}/admin/cache/invalidate-search`, { pattern });
    return res.data;
  }, []);

  const fetchScraperStats = useCallback(async (range = "7d") => {
    try {
      const res = await axios.get(`${API}/admin/scraper-stats`, { params: { range } });
      return res.data;
    } catch { return null; }
  }, []);

  const fetchUserActivity = useCallback(async (range = "7d") => {
    try {
      const res = await axios.get(`${API}/admin/user-activity`, { params: { range } });
      return res.data;
    } catch { return null; }
  }, []);

  const fetchQueueStats = useCallback(async () => {
    try {
      const res = await axios.get(`${API}/admin/queue-stats`);
      return res.data;
    } catch { return null; }
  }, []);

  // On mount (authenticated only): load personas, then signal ready
  useEffect(() => {
    if (!token) return;
    setPersonasReady(false);
    fetchPersonas().then(list => {
      const stored = localStorage.getItem(PERSONA_KEY);
      const valid = list.find(p => p.id === stored);
      if (!valid) {
        setActivePersonaIdState(null);
        localStorage.removeItem(PERSONA_KEY);
      }
      setPersonasReady(true);
    });
  }, [token, fetchPersonas]);

  // Cleanup polling on unmount
  useEffect(() => {
    return () => stopScoringPoll();
  }, [stopScoringPoll]);

  const hasResume = Boolean(profile?.resume_r2_key);
  const needsResumeReupload = Boolean(profile?.raw_text && !profile?.resume_r2_key);

  return (
    <AppContext.Provider value={{
      user, token, login, register, logout,
      profile, jobs, selectedJob, stats, searchHistory, documents, loading, lastSearchId, lastSearchResult, unreadAlerts,
      hasResume, needsResumeReupload,
      totalJobsCount, currentPage, totalPages, pageSize, scoringProgress,
      // persona
      personas, activePersonaId, activePersona, showPersonaSelector, personasReady,
      setActivePersonaId, fetchPersonas, createPersona, updatePersona, deletePersona,
      uploadPersonaResume, setShowPersonaSelector,
      // core
      setSelectedJob, uploadResume, fetchProfile, searchJobs, fetchJobs,
      updateJobStatus, scoreJob, scoreBatch, generateContent,
      fetchStats, fetchSearchHistory, fetchDocuments, deleteJob,
      fetchScoreDistribution, scoreAllUnscored, fetchScoringStatus,
      startScoringPoll, stopScoringPoll,
      researchCompany, findHiringManagers, scrapeCareerPage, deepScrape, fetchSystemStatus,
      fetchAlertCount, fetchAlerts, markAlertsRead, deleteAlert,
      getRadarConfig, saveRadarConfig, runRadarNow,
      // watchlist
      fetchWatchlist, addWatchlistCompany, removeWatchlistCompany,
      toggleWatchlistPause, scanWatchlistCompany, fetchWatchlistJobs,
      // settings
      fetchSettings, saveSettings,
      // auto-apply
      fetchAutoApplyCandidates, scanAllWatchlist, fetchAutomationStatus, fetchActivityLog,
      // admin
      isAdmin, inviteUser, listUsers, fetchAdminTelemetry,
      getAdminUser, disableUser, enableUser, deleteAdminUser, adminGetResume,
      fetchResendUsage, fetchLLMConfig, updateLLMConfig, overrideCircuitBreaker,
      fetchSkillGraphStats, rebuildSkillGraph, fetchCeleryHealth,
      fetchCronJobs, adminRunRadarNow, adminRunWatchlistNow, adminSaveRadarConfig,
      adminSaveWatchlistConfig, adminSaveAutoApplyConfig, fetchAlertStats,
      fetchApiUsage, fetchScraperToggles, updateScraperToggles,
      fetchLLMEngineConfig, saveLLMEngineConfig,
      fetchScraperStats, fetchUserActivity, fetchQueueStats,
      // error state
      error, setError,
    }}>
      {children}
    </AppContext.Provider>
  );
}

export const useApp = () => useContext(AppContext);
